// Stripe subscription proration math, implemented to match Stripe exactly.
//
// Semantics (docs.stripe.com/billing/subscriptions/prorations and
// /billing/scripts/prorations, refined by the empirical parity run below):
//
// - Stripe prorates TO THE SECOND:
//     factor = (period_end − proration_date) / (period_end − period_start)
// - For each subscription item changed by an update, Stripe creates up to two
//   proration invoice items, each independently rounded to the nearest cent:
//     credit (≤ 0) for unused time on a removed item:  −round(amount × factor)
//     debit  (≥ 0) for remaining time on an added item: +round(amount × factor)
// - Proration items are themselves `discountable=false`, but the
//   subscription-level percent coupon is baked INTO the proration item
//   amounts. When the same update call also changes the coupon:
//     credits bake the coupon the item was ORIGINALLY BILLED under (the
//       pre-update state) — on both the classic and flexible billing engines;
//     debits bake the POST-UPDATE coupon state.
//   Verified cent-for-cent against test-mode Stripe (API 2026-04-22.dahlia)
//   by scripts/billing/verify-proration-parity.ts in node-backend; a literal
//   reading of the docs' "modified discount state" is wrong for credits.
// - Per proration_behavior:
//     always_invoice    → lines are invoiced immediately; a positive net is
//                         charged today, a negative net becomes customer
//                         balance credit (not a refund).
//     create_prorations → the same lines are created but land on the next
//                         cycle's invoice; nothing is charged today.
//     none              → no proration lines at all.
//
// All amounts are integer cents; all times are unix epoch seconds. Everything
// here is pure so display estimates, backend previews, and parity tests share
// one implementation.

import type { StripeProrationBehavior } from "@intelligems/ig-types/dtos/billing/commands/provider-execution";

export type StripeProrationItemInput = {
  // Tier-resolved, post-quantity, pre-coupon amount billed per period, in the
  // smallest currency unit (cents).
  amountCents: number;
  priceId?: string;
};

export type ComputeStripeProrationInput = {
  // Items REMOVED by the update (credited for unused time).
  oldItems: StripeProrationItemInput[];
  // Items ADDED by the update (debited for remaining time).
  newItems: StripeProrationItemInput[];
  // Subscription-level percent coupon (0–100, Stripe's `percent_off`) before
  // and after the same update call. Omitted means no coupon on that side.
  // Credits bake the old percent, debits the new one (see module header).
  oldCouponPercent?: number;
  newCouponPercent?: number;
  periodStartSec: number;
  periodEndSec: number;
  prorationDateSec: number;
  prorationBehavior: StripeProrationBehavior;
};

export type StripeProrationResult = {
  // Sum of per-line-rounded credits, ≤ 0.
  creditCents: number;
  // Sum of per-line-rounded debits, ≥ 0.
  debitCents: number;
  // creditCents + debitCents; null iff prorationBehavior === "none".
  netCents: number | null;
  // True only under always_invoice with a positive net — the amount actually
  // charged today. A negative always_invoice net is a customer balance credit.
  chargedToday: boolean;
  appliedAt: "immediate" | "next_invoice" | "none";
  secondsRemaining: number;
  secondsTotal: number;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const couponMultiplier = (couponPercent: number | undefined) =>
  1 - clamp(couponPercent ?? 0, 0, 100) / 100;

// One proration line, rounded to the cent independently like Stripe does.
// Rounding the positive magnitude and applying the sign matches the docs
// worked example (credit −$19.75 / debit +$39.50 for a $30→$60 change at
// factor 19.75/30).
const prorationLineCents = (
  amountCents: number,
  couponPercent: number | undefined,
  factor: number
) => Math.round(amountCents * couponMultiplier(couponPercent) * factor);

export function computeStripeProration(
  input: ComputeStripeProrationInput
): StripeProrationResult {
  const {
    oldItems,
    newItems,
    oldCouponPercent,
    newCouponPercent,
    periodStartSec,
    periodEndSec,
    prorationDateSec,
    prorationBehavior,
  } = input;

  const secondsTotal = Math.max(0, periodEndSec - periodStartSec);
  const secondsRemaining = clamp(
    periodEndSec - prorationDateSec,
    0,
    secondsTotal
  );
  const factor = secondsTotal > 0 ? secondsRemaining / secondsTotal : 0;

  if (prorationBehavior === "none") {
    return {
      creditCents: 0,
      debitCents: 0,
      netCents: null,
      chargedToday: false,
      appliedAt: "none",
      secondsRemaining,
      secondsTotal,
    };
  }

  // Credits bake the coupon the item was originally billed under — parity-
  // verified on both billing engines (see module header).
  const creditCents = oldItems.reduce(
    (sum, item) =>
      sum - prorationLineCents(item.amountCents, oldCouponPercent, factor),
    0
  );
  const debitCents = newItems.reduce(
    (sum, item) =>
      sum + prorationLineCents(item.amountCents, newCouponPercent, factor),
    0
  );
  const netCents = creditCents + debitCents;

  return {
    creditCents,
    debitCents,
    netCents,
    chargedToday: prorationBehavior === "always_invoice" && netCents > 0,
    appliedAt:
      prorationBehavior === "always_invoice" ? "immediate" : "next_invoice",
    secondsRemaining,
    secondsTotal,
  };
}

const SECONDS_PER_DAY = 86_400;

// Day counts for "X of Y days left in your current cycle", derived from the
// SAME seconds values as the dollar math so the two can never disagree.
// `daysRemaining` floors (a partial day has already started being consumed);
// `totalDays` rounds (calendar months are 28–31 days, DST cycles are ±1h).
export function prorationDayDisplay({
  secondsRemaining,
  secondsTotal,
}: Pick<StripeProrationResult, "secondsRemaining" | "secondsTotal">): {
  daysRemaining: number;
  totalDays: number;
} {
  return {
    daysRemaining: Math.floor(secondsRemaining / SECONDS_PER_DAY),
    totalDays: Math.round(secondsTotal / SECONDS_PER_DAY),
  };
}

// Minimal structural view of Stripe.Price.Tier — ig-utils does not depend on
// the stripe SDK.
export type StripePriceTierInput = {
  up_to: number | null;
  flat_amount?: number | null;
  unit_amount?: number | null;
};

// Resolves the amount (cents) Stripe bills per period for one subscription
// item. Modular prices are VOLUME-tiered: the item's quantity selects exactly
// one tier and the amount is that tier's `flat_amount` plus its `unit_amount`
// × quantity (modular tiers use flat_amount with unit_amount 0). Non-tiered
// prices fall back to `unit_amount × quantity`. Returns null when neither
// tiers nor a unit amount are available — callers must treat that as "cannot
// estimate", never as zero.
export function resolveTieredItemAmountCents(options: {
  quantity?: number | null;
  tiers?: StripePriceTierInput[] | null;
  unitAmountCents?: number | null;
}): number | null {
  const quantity = options.quantity ?? 1;

  const tiers = options.tiers;
  if (tiers && tiers.length > 0) {
    const sorted = [...tiers].sort((a, b) => {
      if (a.up_to == null) {
        return 1;
      }
      if (b.up_to == null) {
        return -1;
      }
      return a.up_to - b.up_to;
    });
    const tier =
      sorted.find((t) => t.up_to != null && quantity <= t.up_to) ??
      sorted.find((t) => t.up_to == null) ??
      sorted[sorted.length - 1];

    return (tier.flat_amount ?? 0) + (tier.unit_amount ?? 0) * quantity;
  }

  if (options.unitAmountCents != null) {
    return options.unitAmountCents * quantity;
  }

  return null;
}
